import React, { useState, useEffect } from 'react';
import { SCRAPED_REVIEWS } from '../data/company';

export default function ReviewsSlider() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [paused, setPaused] = useState(false);

  const totalReviews = SCRAPED_REVIEWS.length;

  const goToPrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev === 0 ? totalReviews - 1 : prev - 1));
    setTimeout(() => setIsAnimating(false), 300);
  };

  const goToNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev === totalReviews - 1 ? 0 : prev + 1));
    setTimeout(() => setIsAnimating(false), 300);
  };

  useEffect(() => {
    if (paused) return;
    const interval = setInterval(() => {
      goToNext();
    }, 6000);
    return () => clearInterval(interval);
  }, [currentIndex, paused, isAnimating]);

  const currentReview = SCRAPED_REVIEWS[currentIndex];

  return (
    <section className="py-16 sm:py-20 bg-slate-50 border-y border-slate-200/80 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-bold uppercase tracking-wider mb-3">
            <i className="fa-solid fa-quote-left text-blue-600" aria-hidden="true" />
            <span>Kundeudtalelser</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            Hvad vores kunder siger om os
          </h2>
          <p className="mt-2 text-slate-600 text-sm sm:text-base">
            Vi opbygger langvarige relationer baseret på tillid, grundighed og god service.
          </p>
        </div>

        {/* Single Review Slider Container */}
        <div 
          className="max-w-3xl mx-auto relative px-4 sm:px-12"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Main Card */}
          <div className="bg-white p-8 sm:p-12 rounded-3xl shadow-xl border border-slate-200/80 relative min-h-[220px] flex flex-col justify-between overflow-hidden">
            
            {/* Animated Slide Content Wrapper */}
            <div 
              key={currentIndex}
              className="transition-all duration-300 ease-out animate-in fade-in slide-in-from-right-4"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-1 text-amber-400">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="fa-solid fa-star text-base" aria-hidden="true" />
                  ))}
                </div>
                <i className="fa-solid fa-quote-right text-blue-100 text-4xl" aria-hidden="true" />
              </div>

              <blockquote className="text-slate-800 italic text-base sm:text-xl font-medium leading-relaxed mb-8">
                "{currentReview.comment}"
              </blockquote>

              <div className="pt-6 border-t border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-600 text-white font-extrabold flex items-center justify-center text-base shadow-md">
                    {currentReview.author[0]}
                  </div>
                  <div>
                    <span className="font-extrabold text-slate-900 block text-base">– {currentReview.author}</span>
                    <span className="text-xs text-slate-500 font-medium">Kunde hos OP Rengøringsservice</span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Navigation Controls */}
          <div className="flex items-center justify-between mt-8">
            <button
              type="button"
              onClick={goToPrev}
              className="w-11 h-11 rounded-full bg-white hover:bg-blue-600 hover:text-white border border-slate-200 shadow-md text-slate-700 transition-all flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Forrige anmeldelse"
            >
              <i className="fa-solid fa-chevron-left text-sm" aria-hidden="true" />
            </button>

            {/* Pagination Dots */}
            <div className="flex items-center gap-2">
              {SCRAPED_REVIEWS.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    if (isAnimating) return;
                    setIsAnimating(true);
                    setCurrentIndex(idx);
                    setTimeout(() => setIsAnimating(false), 300);
                  }}
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    currentIndex === idx ? 'w-8 bg-blue-600' : 'w-2.5 bg-slate-300 hover:bg-slate-400'
                  }`}
                  aria-label={`Gå til anmeldelse ${idx + 1}`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={goToNext}
              className="w-11 h-11 rounded-full bg-white hover:bg-blue-600 hover:text-white border border-slate-200 shadow-md text-slate-700 transition-all flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Næste anmeldelse"
            >
              <i className="fa-solid fa-chevron-right text-sm" aria-hidden="true" />
            </button>
          </div>

        </div>
      </div>
    </section>
  );
}
